"use client";

import Image from "next/image";
import Link from "next/link";
import { useTranslations, useLocale } from "next-intl";
import {
  Plane,
  Landmark,
  Crown,
  Info,
  ChevronRight,
  Clock,
  Phone,
  MapPin,
  Wifi,
  Brain,
  HeartPulse,
  Baby,
  Dog,
  Map as MapIcon,
  Building2,
  Home,
} from "lucide-react";
import MapButton from "../../components/MapButton";

const hubCards = [
  {
    key: "servaero",
    href: "/informacion/servicios-aeroportuarios",
    icon: Plane,
    accent: "from-[#102542] to-[#1B3B5F]",
    iconBg: "bg-[#ffb800]/15 text-[#ffb800]",
    darkText: false,
  },
  {
    key: "servfin",
    href: "/informacion/servicios-financieros",
    icon: Landmark,
    accent: "from-[#1B3B5F] to-[#2C5282]",
    iconBg: "bg-white/15 text-white",
    darkText: false,
  },
  {
    key: "salvip",
    href: "/informacion/salasvip",
    icon: Crown,
    accent: "from-[#ffb800] to-[#e6a700]",
    iconBg: "bg-[#102542]/15 text-[#102542]",
    darkText: true,
  },
  {
    key: "centinfo",
    href: "/informacion/centros-informacion",
    icon: Info,
    accent: "from-[#4F6D94] to-[#102542]",
    iconBg: "bg-[#ffb800]/15 text-[#ffb800]",
    darkText: false,
  },
] as const;

const quickInfo = [
  { key: "schedule", icon: Clock },
  { key: "contact", icon: Phone },
  { key: "terminals", icon: MapPin },
  { key: "wifi", icon: Wifi },
] as const;

const travelGuides = [
  {
    key: "children",
    href: "/informacion/viajando-con-menores",
    icon: Baby,
    image:
      "https://galeria.tocumenaero.com/gallery/api/Movimiento%20de%20pasajeros/duty4.jpg",
  },
  {
    key: "pets",
    href: "/informacion/mascotas",
    icon: Dog,
    image:
      "https://galeria.tocumenaero.com/gallery/api/Ingreso de mascotas/9M1A4697.jpg",
  },
  {
    key: "map",
    href: "/map",
    icon: MapIcon,
    image:
      "https://galeria.tocumenaero.com/gallery/api/Panor%C3%A1micas%20internas/9M1A0559.jpg",
  },
  {
    key: "entities",
    href: "/informacion/entidades",
    icon: Building2,
    image: "https://galeria.tocumenaero.com/gallery/api/SIGAT/321A7965.jpg",
  },
] as const;

const passengerServices = [
  {
    key: "sensory",
    icon: Brain,
    url: "https://airportpanama.lmp.prod.livingmap.com/poi?feature=9c4a390852818e2ddb59a82ec0300c8f&floor=2&lang=en-GB#19/9.0660503/-79.3881734/119",
  },
  {
    key: "medical",
    icon: HeartPulse,
    url: "https://airportpanama.lmp.prod.livingmap.com/poi?feature=2b101a333fc814d429433f148c62965e&floor=2&lang=en-GB#19/9.0662252/-79.3887007/119",
  },
  {
    key: "pets",
    icon: Dog,
    url: "https://airportpanama.lmp.prod.livingmap.com/poi?feature=bd8df819376a903fe8c13744df3f110a&floor=2&lang=en-GB#19/9.0671717/-79.3866085/119",
  },
  {
    key: "family",
    icon: Baby,
    url: "https://airportpanama.lmp.prod.livingmap.com/poi?feature=e63e063bb46dafad03759e480da4ef85&floor=2&lang=en-GB#19/9.0663176/-79.3870959/119",
  },
] as const;

export default function Informacion() {
  const t = useTranslations("informacionPage");
  const tCrumb = useTranslations("breadcrumb");
  const tNav = useTranslations("nav.informacion.links");
  const locale = useLocale();

  return (
    <main className="bg-white  mx-auto">
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src="https://galeria.tocumenaero.com/gallery/api/Movimiento%20de%20pasajeros/321A7269.jpg"
          alt={t("hero.title")}
          fill
          priority
          sizes="100vw"
          className="object-cover blur-xs"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />

        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-xs text-white/80 flex-wrap">
              <li>
                <Link
                  href={`/${locale}`}
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
                >
                  <Home className="w-3.5 h-3.5" />
                  <span>{tCrumb("home")}</span>
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5 text-white/40" />
              </li>
              <li
                aria-current="page"
                className="text-white font-medium truncate"
              >
                {tNav("informacion")}
              </li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px bg-[#ffb800]" />
              {t("hero.eyebrow")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              {t("hero.title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("hero.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* 4 Cards Principales */}
      <section className="w-full py-16 md:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#102542] mb-3">
              {t("hubSection.title")}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t("hubSection.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {hubCards.map(
              ({ key, href, icon: Icon, accent, iconBg, darkText }) => (
                <Link
                  key={key}
                  href={href}
                  className={`group relative rounded-2xl p-6 bg-gradient-to-br ${accent} shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden`}
                >
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/0 to-white/0 group-hover:to-white/10 transition-all duration-700 pointer-events-none" />

                  <div className="relative z-10 flex flex-col h-full min-h-[260px]">
                    <div
                      className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 ${iconBg} transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6`}
                    >
                      <Icon className="w-7 h-7" />
                    </div>

                    <h3
                      className={`text-xl font-bold mb-3 ${
                        darkText ? "text-[#102542]" : "text-white"
                      }`}
                    >
                      {t(`hubCards.${key}.title`)}
                    </h3>
                    <p
                      className={`text-sm leading-relaxed mb-6 flex-1 ${
                        darkText ? "text-[#102542]/80" : "text-white/85"
                      }`}
                    >
                      {t(`hubCards.${key}.description`)}
                    </p>

                    <div
                      className={`inline-flex items-center gap-1 text-sm font-semibold transition-all duration-300 group-hover:gap-2 ${
                        darkText ? "text-[#102542]" : "text-white"
                      }`}
                    >
                      {t("hubSection.seeMore")}
                      <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              ),
            )}
          </div>
        </div>
      </section>

      {/* Guías rápidas - viajes y recursos */}
      <section className="w-full py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#ffb800] text-sm uppercase tracking-widest font-semibold">
              {t("guidesSection.tag")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#102542] mt-3 mb-3">
              {t("guidesSection.title")}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t("guidesSection.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {travelGuides.map(({ key, href, icon: Icon, image }) => {
              const title = t(`travelGuides.${key}.title`);
              return (
                <Link
                  key={key}
                  href={href}
                  className="group relative h-[340px] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                >
                  <Image
                    src={image}
                    alt={title}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#102542] via-[#102542]/70 to-[#102542]/20 group-hover:from-[#102542] group-hover:via-[#102542]/80 transition-all duration-500" />

                  <div className="relative z-10 h-full p-6 flex flex-col justify-end text-white">
                    <div className="w-12 h-12 rounded-xl bg-[#ffb800] text-[#102542] flex items-center justify-center mb-4 transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 drop-shadow-md">
                      {title}
                    </h3>
                    <p className="text-sm text-white/85 leading-relaxed mb-4 line-clamp-3">
                      {t(`travelGuides.${key}.description`)}
                    </p>
                    <div className="inline-flex items-center gap-1 text-sm font-semibold text-[#ffb800] transition-all duration-300 group-hover:gap-2">
                      {t("guidesSection.seeMore")}
                      <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Información rápida */}
      <section className="w-full py-16 md:py-20 bg-[#102542] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 right-20 w-80 h-80 bg-[#ffb800] rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <span className="text-[#ffb800] text-sm uppercase tracking-widest font-semibold">
                {t("quickInfoSection.tag")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
                {t("quickInfoSection.title")}
              </h2>
              <p className="text-white/70 leading-relaxed">
                {t("quickInfoSection.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {quickInfo.map(({ key, icon: Icon }) => (
                <div
                  key={key}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 hover:border-[#ffb800]/40 transition-all duration-300 flex flex-col"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#ffb800]/15 text-[#ffb800] flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-white font-semibold mb-1">
                    {t(`quickInfo.${key}.title`)}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed flex-1">
                    {t(`quickInfo.${key}.description`)}
                  </p>
                  {key === "wifi" && (
                    <Link
                      href="/informacion/wifi"
                      className="mt-4 inline-flex items-center gap-1 self-start text-sm font-semibold text-[#ffb800] hover:gap-2 transition-all duration-300"
                    >
                      {t("quickInfoSection.viewMore")}
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Servicios para el pasajero */}
      <section className="w-full py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#ffb800] text-sm uppercase tracking-widest font-semibold">
              {t("passengerSection.tag")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#102542] mt-3 mb-3">
              {t("passengerSection.title")}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t("passengerSection.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {passengerServices.map(({ key, icon: Icon, url }) => (
              <div
                key={key}
                className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:border-[#ffb800]/40 transition-all duration-500 hover:-translate-y-1 flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-[#102542] text-[#ffb800] flex items-center justify-center mb-4 group-hover:bg-[#ffb800] group-hover:text-[#102542] transition-all duration-500">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-[#102542] mb-2">
                  {t(`passengerServices.${key}.title`)}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-4">
                  {t(`passengerServices.${key}.description`)}
                </p>
                <MapButton
                  src={url}
                  variant="link"
                  label={t("passengerSection.viewMap")}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="w-full py-16 md:py-20 bg-gradient-to-br from-[#ffb800] to-[#e6a700]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#102542] mb-4">
            {t("cta.title")}
          </h2>
          <p className="text-[#102542]/80 text-lg mb-8 max-w-2xl mx-auto">
            {t("cta.description")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/informacion/centros-informacion"
              className="inline-flex items-center justify-center gap-2 bg-[#102542] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#1B3B5F] transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
            >
              <Info className="w-5 h-5" />
              {t("cta.infoCenter")}
            </Link>
            <Link
              href="/buzon-sugerencias"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#102542] px-8 py-4 rounded-full font-semibold hover:bg-[#102542] hover:text-white transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 border-2 border-[#102542]"
            >
              {t("cta.sendQuery")}
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
